import { useEffect, useMemo, useState } from 'react'
import { Link } from 'wouter'
import { useTenant } from '../../contexts/TenantContext'
import { CalendarWeekPicker } from '../../components/CalendarWeekPicker'

type SchemaHealthResponse = {
  ok: boolean
  requiredTables: Record<string, boolean>
  optionalColumns?: {
    agent_traces?: {
      error_code?: boolean
      metadata?: boolean
    }
  }
  resolvedCompanyId?: string
  error?: string
}

type HttpErrorDetail = {
  status: number
  text: string
}

type SmokeRunResponse = {
  ok: boolean
  schema?: {
    requiredTables?: Record<string, boolean>
    optionalColumns?: any
  }
  runId?: string | null
  artifactId?: string | null
  createdCount?: number
  notes?: string[]
  error?: string
}

type BeliefConfig = {
  version: string
  weights: {
    coverage_of_signals: number
    confidence_of_signals: number
    consistency_across_channels: number
    historical_frequency: number
  }
  belief_weights: Record<string, { base: number }>
  thresholds: {
    primary_confidence_min: number
    exception_materiality_pct: number
  }
  suppression: {
    suppress_supporting_findings: boolean
    suppress_redundant_findings: boolean
  }
}

type BeliefBacktestResponse = {
  ok: boolean
  summary?: {
    totalWeeks: number
    accuracyByBelief: Record<string, number>
    falseAlarmRate: number
    exceptionCorrectRate: number
    topMisses: Array<{
      calendarWeekKey: string | null
      beliefPrimary: string
      beliefScore: number
      nextDirection: string
      expectedDirection: string
      fit: boolean | null
    }>
  }
  results?: any[]
  error?: string
}

const DEFAULT_BELIEF_CONFIG: BeliefConfig = {
  version: 'v1',
  weights: {
    coverage_of_signals: 0.4,
    confidence_of_signals: 0.3,
    consistency_across_channels: 0.2,
    historical_frequency: 0.1
  },
  belief_weights: {
    post_promo_normalization: { base: 1.0 },
    forecast_miss_model_drift: { base: 1.0 },
    execution_constraint: { base: 1.0 },
    demand_softening: { base: 1.0 },
    marketing_inefficiency: { base: 1.0 },
    growth_opportunity: { base: 1.0 }
  },
  thresholds: {
    primary_confidence_min: 0.65,
    exception_materiality_pct: 0.1
  },
  suppression: {
    suppress_supporting_findings: true,
    suppress_redundant_findings: true
  }
}

const SESSION_KEY = 'patterniq_auth_session'

function getAuthToken(): string | null {
  try {
    const sessionData = localStorage.getItem(SESSION_KEY)
    if (!sessionData) return null
    const session = JSON.parse(sessionData)
    if (session.expiresAt && session.expiresAt < Date.now()) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return session.token || null
  } catch {
    return null
  }
}

async function fetchJsonWithAuth<T>(
  url: string,
  params: { method: 'GET' | 'POST'; body?: any; timeoutMs?: number } = { method: 'GET' }
): Promise<{ ok: true; status: number; json: T } | { ok: false; status: number; text: string }> {
  const controller = new AbortController()
  const timeoutMs = params.timeoutMs ?? 10_000
  const to = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const token = getAuthToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`

    const resp = await fetch(url, {
      method: params.method,
      headers,
      body: params.method === 'POST' ? JSON.stringify(params.body ?? {}) : undefined,
      credentials: 'include',
      signal: controller.signal
    })

    if (!resp.ok) {
      const text = await resp.text().catch(() => '')
      return { ok: false, status: resp.status, text }
    }

    const json = (await resp.json()) as T
    return { ok: true, status: resp.status, json }
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      return { ok: false, status: 0, text: 'Request timed out' }
    }
    return { ok: false, status: 0, text: e?.message || 'Network error' }
  } finally {
    clearTimeout(to)
  }
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
        ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {label}
    </span>
  )
}

function normalizeBeliefWeights(weights: BeliefConfig['weights']): BeliefConfig['weights'] {
  const total =
    (weights.coverage_of_signals || 0) +
    (weights.confidence_of_signals || 0) +
    (weights.consistency_across_channels || 0) +
    (weights.historical_frequency || 0)
  if (!total || total <= 0) return { ...DEFAULT_BELIEF_CONFIG.weights }
  return {
    coverage_of_signals: weights.coverage_of_signals / total,
    confidence_of_signals: weights.confidence_of_signals / total,
    consistency_across_channels: weights.consistency_across_channels / total,
    historical_frequency: weights.historical_frequency / total
  }
}

export default function ReleaseHealth() {
  const { companyId } = useTenant()
  const [schema, setSchema] = useState<SchemaHealthResponse | null>(null)
  const [schemaLoading, setSchemaLoading] = useState(false)
  const [schemaError, setSchemaError] = useState<string | null>(null)
  const [schemaHttpError, setSchemaHttpError] = useState<HttpErrorDetail | null>(null)

  const [smoke, setSmoke] = useState<SmokeRunResponse | null>(null)
  const [smokeLoading, setSmokeLoading] = useState(false)
  const [smokeError, setSmokeError] = useState<string | null>(null)
  const [smokeHttpError, setSmokeHttpError] = useState<HttpErrorDetail | null>(null)

  const [beliefConfig, setBeliefConfig] = useState<BeliefConfig | null>(null)
  const [beliefLoading, setBeliefLoading] = useState(false)
  const [beliefSaving, setBeliefSaving] = useState(false)
  const [beliefError, setBeliefError] = useState<string | null>(null)
  const [backtestLoading, setBacktestLoading] = useState(false)
  const [backtestError, setBacktestError] = useState<string | null>(null)
  const [backtestResult, setBacktestResult] = useState<BeliefBacktestResponse | null>(null)
  const [backtestWeeks, setBacktestWeeks] = useState(26)

  const missingTables = useMemo(() => {
    const req = schema?.requiredTables || {}
    return Object.entries(req)
      .filter(([_, exists]) => !exists)
      .map(([name]) => name)
  }, [schema])

  const refreshSchema = async () => {
    setSchemaLoading(true)
    setSchemaError(null)
    setSchemaHttpError(null)
    try {
      const resp = await fetchJsonWithAuth<SchemaHealthResponse>('/api/health/schema', {
        method: 'GET',
        timeoutMs: 10_000
      })
      if (!resp.ok) {
        setSchema(null)
        setSchemaHttpError({ status: resp.status, text: resp.text })
        if (resp.status === 404) {
          setSchemaError('ENABLE_ADMIN_HEALTH is not enabled in this environment.')
        } else if (resp.status === 0 && resp.text === 'Request timed out') {
          setSchemaError('Schema check timed out')
        } else {
          setSchemaError('Failed to load schema health')
        }
        return
      }
      setSchema(resp.json)
    } catch (e: any) {
      setSchemaError(e?.message || 'Failed to load schema health')
      setSchema(null)
    } finally {
      setSchemaLoading(false)
    }
  }

  const runSmoke = async () => {
    setSmokeLoading(true)
    setSmokeError(null)
    setSmokeHttpError(null)
    setSmoke(null)
    try {
      // Smoke runs a full supervised workflow - give it 2 minutes
      const resp = await fetchJsonWithAuth<SmokeRunResponse>('/api/health/smoke/run', {
        method: 'POST',
        body: {},
        timeoutMs: 120_000
      })
      if (!resp.ok) {
        setSmokeHttpError({ status: resp.status, text: resp.text })
        if (resp.status === 404) {
          setSmokeError('ENABLE_ADMIN_HEALTH is not enabled in this environment.')
        } else if (resp.status === 0 && resp.text === 'Request timed out') {
          setSmokeError('Smoke run timed out (workflow may still be running)')
        } else {
          setSmokeError('Smoke run failed')
        }
        return
      }
      setSmoke(resp.json)
    } catch (e: any) {
      setSmokeError(e?.message || 'Smoke run failed')
    } finally {
      setSmokeLoading(false)
    }
  }

  const loadBeliefConfig = async () => {
    setBeliefLoading(true)
    setBeliefError(null)
    try {
      const resp = await fetchJsonWithAuth<{ ok: boolean; config: BeliefConfig }>('/api/admin/brain/belief-config', {
        method: 'GET',
        timeoutMs: 10_000
      })
      if (!resp.ok) {
        setBeliefError(resp.status === 404 ? 'ENABLE_ADMIN_HEALTH is not enabled in this environment.' : 'Failed to load belief config')
        setBeliefConfig(null)
        return
      }
      setBeliefConfig(resp.json.config || DEFAULT_BELIEF_CONFIG)
    } catch (e: any) {
      setBeliefError(e?.message || 'Failed to load belief config')
    } finally {
      setBeliefLoading(false)
    }
  }

  const saveBeliefConfig = async () => {
    if (!beliefConfig) return
    setBeliefSaving(true)
    setBeliefError(null)
    try {
      const resp = await fetchJsonWithAuth<{ ok: boolean; config: BeliefConfig }>('/api/admin/brain/belief-config', {
        method: 'PUT',
        body: { config: beliefConfig },
        timeoutMs: 10_000
      })
      if (!resp.ok) {
        setBeliefError(resp.status === 404 ? 'ENABLE_ADMIN_HEALTH is not enabled in this environment.' : 'Failed to save belief config')
        return
      }
      setBeliefConfig(resp.json.config)
    } catch (e: any) {
      setBeliefError(e?.message || 'Failed to save belief config')
    } finally {
      setBeliefSaving(false)
    }
  }

  const runBeliefBacktest = async () => {
    setBacktestLoading(true)
    setBacktestError(null)
    setBacktestResult(null)
    try {
      const resp = await fetchJsonWithAuth<BeliefBacktestResponse>(`/api/admin/brain/belief-backtest?weeks=${backtestWeeks}`, {
        method: 'POST',
        body: {},
        timeoutMs: 60_000
      })
      if (!resp.ok) {
        setBacktestError(resp.status === 404 ? 'ENABLE_ADMIN_HEALTH is not enabled in this environment.' : 'Failed to run backtest')
        return
      }
      setBacktestResult(resp.json)
    } catch (e: any) {
      setBacktestError(e?.message || 'Failed to run backtest')
    } finally {
      setBacktestLoading(false)
    }
  }

  useEffect(() => {
    refreshSchema()
    loadBeliefConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Release &amp; Health <span className="text-xs font-normal text-gray-400">(v2)</span></h1>
          <p className="text-sm text-gray-600">
            Schema validator + one-click smoke run for this deployment. Requires admin access and backend flag{' '}
            <code className="px-1 py-0.5 bg-gray-100 rounded">ENABLE_ADMIN_HEALTH=true</code>.
          </p>
          {(import.meta as any).env?.DEV ? (
            <div className="mt-2 text-xs text-gray-500">
              Resolved companyId: <code className="px-1 py-0.5 bg-gray-100 rounded">{companyId || '—'}</code>
            </div>
          ) : null}
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshSchema}
            disabled={schemaLoading}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-60"
          >
            {schemaLoading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            onClick={runSmoke}
            disabled={smokeLoading || schemaLoading || !schema}
            className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm disabled:opacity-60"
          >
            {smokeLoading ? 'Running…' : 'Run Smoke Now'}
          </button>
        </div>
      </div>

      {/* A) Schema Health */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="font-bold text-gray-900">Schema Health</div>
            <div className="text-xs text-gray-500">Calls GET /api/health/schema</div>
          </div>
          {schema?.ok ? <StatusPill ok={true} label="GREEN" /> : <StatusPill ok={false} label="RED" />}
        </div>
        <div className="p-5 space-y-4">
          {schemaError ? (
            <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
              <div className="font-semibold">Schema check error</div>
              <div className="mt-1">{schemaError}</div>
              {schemaHttpError ? (
                <div className="mt-2 text-xs">
                  <div>
                    <span className="font-semibold">Status:</span> {schemaHttpError.status || '—'}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap break-words">
                    <span className="font-semibold">Response:</span> {schemaHttpError.text || '—'}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(schema?.requiredTables || {}).map(([name, exists]) => (
              <div key={name} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2">
                <div className="text-sm font-medium text-gray-900">{name}</div>
                <StatusPill ok={!!exists} label={exists ? 'OK' : 'MISSING'} />
              </div>
            ))}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">Optional columns</div>
            <div className="flex flex-wrap gap-2">
              <StatusPill
                ok={!!schema?.optionalColumns?.agent_traces?.error_code}
                label={`agent_traces.error_code: ${schema?.optionalColumns?.agent_traces?.error_code ? 'OK' : 'missing'}`}
              />
              <StatusPill
                ok={!!schema?.optionalColumns?.agent_traces?.metadata}
                label={`agent_traces.metadata: ${schema?.optionalColumns?.agent_traces?.metadata ? 'OK' : 'missing'}`}
              />
            </div>
          </div>

          {missingTables.length > 0 ? (
            <div className="text-sm text-red-700">
              Migration required. Missing: <code className="px-1 py-0.5 bg-red-50 rounded">{missingTables.join(', ')}</code>
            </div>
          ) : null}

          {schema?.resolvedCompanyId ? (
            <div className="border-t border-gray-200 pt-4">
              <div className="text-sm font-semibold text-gray-900 mb-2">Tenant Context</div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Resolved companyId:</span>
                <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{schema.resolvedCompanyId}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(schema.resolvedCompanyId || '')
                    // Quick visual feedback (optional)
                  }}
                  className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Use this value for SMOKE_COMPANY_ID in automation.</p>
            </div>
          ) : null}
        </div>
      </div>

      {/* B) Smoke Test */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="font-bold text-gray-900">Smoke Test</div>
            <div className="text-xs text-gray-500">Calls POST /api/health/smoke/run</div>
          </div>
          {smoke ? <StatusPill ok={!!smoke.ok} label={smoke.ok ? 'PASS' : 'FAIL'} /> : <div className="text-xs text-gray-500">—</div>}
        </div>
        <div className="p-5 space-y-3">
          {smokeError ? (
            <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
              <div className="font-semibold">Smoke run error</div>
              <div className="mt-1">{smokeError}</div>
              {smokeHttpError ? (
                <div className="mt-2 text-xs">
                  <div>
                    <span className="font-semibold">Status:</span> {smokeHttpError.status || '—'}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap break-words">
                    <span className="font-semibold">Response:</span> {smokeHttpError.text || '—'}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {smoke ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-500">runId</div>
                  <div className="text-sm font-mono text-gray-900 break-all">{smoke.runId || '—'}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-500">artifactId</div>
                  <div className="text-sm font-mono text-gray-900 break-all">{smoke.artifactId || '—'}</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-500">createdCount</div>
                  <div className="text-sm font-semibold text-gray-900">{smoke.createdCount ?? 0}</div>
                </div>
              </div>

              {Array.isArray(smoke.notes) && smoke.notes.length > 0 ? (
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-2">notes</div>
                  <ul className="text-sm text-gray-800 list-disc pl-5 space-y-1">
                    {smoke.notes.map((n, idx) => (
                      <li key={idx}>{n}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-sm text-gray-600">Click “Run Smoke Now” to execute buyer_meeting_pack → save artifact → create actions.</div>
          )}
        </div>
      </div>

      {/* C) Belief Engine Tuning */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="font-bold text-gray-900">Belief Engine Tuning</div>
            <div className="text-xs text-gray-500">Tenant-scoped weights, thresholds, and backtest</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBeliefConfig(DEFAULT_BELIEF_CONFIG)}
              disabled={beliefLoading || beliefSaving}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-60"
            >
              Reset Defaults
            </button>
            <button
              onClick={saveBeliefConfig}
              disabled={beliefLoading || beliefSaving || !beliefConfig}
              className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-60"
            >
              {beliefSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <div className="p-5 space-y-5">
          {beliefError ? (
            <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
              <div className="font-semibold">Belief config error</div>
              <div className="mt-1">{beliefError}</div>
            </div>
          ) : null}

          {beliefLoading && !beliefConfig ? (
            <div className="text-sm text-gray-500">Loading belief config…</div>
          ) : null}

          {beliefConfig ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="text-sm font-semibold text-gray-900">Weights (auto-normalized)</div>
                  {Object.entries(beliefConfig.weights).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{key.replace(/_/g, ' ')}</span>
                        <span>{value.toFixed(2)}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={value}
                        onChange={(e) => {
                          const next = {
                            ...beliefConfig.weights,
                            [key]: parseFloat(e.target.value)
                          }
                          setBeliefConfig({
                            ...beliefConfig,
                            weights: normalizeBeliefWeights(next)
                          })
                        }}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>

                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="text-sm font-semibold text-gray-900">Thresholds</div>
                  <div className="space-y-2">
                    <label className="flex items-center justify-between text-sm text-gray-700">
                      <span>Primary Confidence Min</span>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={beliefConfig.thresholds.primary_confidence_min}
                        onChange={(e) =>
                          setBeliefConfig({
                            ...beliefConfig,
                            thresholds: {
                              ...beliefConfig.thresholds,
                              primary_confidence_min: parseFloat(e.target.value)
                            }
                          })
                        }
                        className="w-24 border border-gray-200 rounded px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="flex items-center justify-between text-sm text-gray-700">
                      <span>Exception Materiality %</span>
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={beliefConfig.thresholds.exception_materiality_pct}
                        onChange={(e) =>
                          setBeliefConfig({
                            ...beliefConfig,
                            thresholds: {
                              ...beliefConfig.thresholds,
                              exception_materiality_pct: parseFloat(e.target.value)
                            }
                          })
                        }
                        className="w-24 border border-gray-200 rounded px-2 py-1 text-sm"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="text-sm font-semibold text-gray-900">Belief Base Multipliers</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(beliefConfig.belief_weights).map(([belief, cfg]) => (
                    <label key={belief} className="flex items-center justify-between text-sm text-gray-700">
                      <span>{belief.replace(/_/g, ' ')}</span>
                      <input
                        type="number"
                        min={0.5}
                        max={2}
                        step={0.1}
                        value={cfg.base}
                        onChange={(e) =>
                          setBeliefConfig({
                            ...beliefConfig,
                            belief_weights: {
                              ...beliefConfig.belief_weights,
                              [belief]: { base: parseFloat(e.target.value) }
                            }
                          })
                        }
                        className="w-24 border border-gray-200 rounded px-2 py-1 text-sm"
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 space-y-2">
                <div className="text-sm font-semibold text-gray-900">Suppression Rules</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={beliefConfig.suppression.suppress_supporting_findings}
                    onChange={(e) =>
                      setBeliefConfig({
                        ...beliefConfig,
                        suppression: {
                          ...beliefConfig.suppression,
                          suppress_supporting_findings: e.target.checked
                        }
                      })
                    }
                  />
                  Suppress supporting findings
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={beliefConfig.suppression.suppress_redundant_findings}
                    onChange={(e) =>
                      setBeliefConfig({
                        ...beliefConfig,
                        suppression: {
                          ...beliefConfig.suppression,
                          suppress_redundant_findings: e.target.checked
                        }
                      })
                    }
                  />
                  Suppress redundant findings
                </label>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Historical Backtest</div>
                    <div className="text-xs text-gray-500">Runs DBE scoring over past weeks</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={4}
                      max={104}
                      value={backtestWeeks}
                      onChange={(e) => setBacktestWeeks(parseInt(e.target.value, 10))}
                      className="w-20 border border-gray-200 rounded px-2 py-1 text-sm"
                    />
                    <button
                      onClick={runBeliefBacktest}
                      disabled={backtestLoading}
                      className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60"
                    >
                      {backtestLoading ? 'Running…' : 'Run Backtest'}
                    </button>
                  </div>
                </div>

                {backtestError ? (
                  <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
                    <div className="font-semibold">Backtest error</div>
                    <div className="mt-1">{backtestError}</div>
                  </div>
                ) : null}

                {backtestResult?.summary ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Weeks analyzed</span>
                      <span className="font-medium">{backtestResult.summary.totalWeeks}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">False alarm rate</span>
                      <span className="font-medium">{Math.round(backtestResult.summary.falseAlarmRate * 100)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Exception correctness</span>
                      <span className="font-medium">{Math.round(backtestResult.summary.exceptionCorrectRate * 100)}%</span>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Accuracy by belief</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(backtestResult.summary.accuracyByBelief || {}).map(([belief, acc]) => (
                          <div key={belief} className="flex items-center justify-between text-xs text-gray-700">
                            <span>{belief.replace(/_/g, ' ')}</span>
                            <span className="font-medium">{Math.round(acc * 100)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No backtest results yet.</div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* D) Case Gating Thresholds (PIQ-V1) */}
      <CaseGatingThresholdsPanel companyId={companyId} />

      {/* D.5) Belief Accuracy (PIQ-V1) */}
      <BeliefAccuracyPanel companyId={companyId} />

      {/* E) Outcome Evaluation (V3-3B) */}
      <OutcomeEvaluationPanel companyId={companyId} />

      {/* F) Learning & Calibration (V3-3C) */}
      <LearningVisibilityPanel companyId={companyId} />

      {/* G) Scenario Seeds (GA-R2D) */}
      <ScenarioSeedsPanel />

      {/* H) Brain Queue Health (V3-5B) */}
      <BrainQueueHealthPanel companyId={companyId} />

      {/* I) Weekly Actuals Backfill (PIQ-V2) */}
      <WeeklyActualsBackfillPanel companyId={companyId} />

      {/* I.5) Snapshot Completeness (V2.8) */}
      <SnapshotCompletenessPanel companyId={companyId} />

      {/* J) Belief Accuracy Replay (PIQ-V2 Phase 3) */}
      <BeliefAccuracyReplayPanel companyId={companyId} />

      {/* K) Links */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200">
          <div className="font-bold text-gray-900">Links</div>
        </div>
        <div className="p-5 flex flex-col gap-2 text-sm">
          <Link href="/command-center">
            <a className="text-violet-700 hover:underline">Open Command Center (Workflow Artifacts)</a>
          </Link>
          {smoke?.runId ? (
            <a
              className="text-violet-700 hover:underline"
              href={`/api/agent-core/evidence/run?runId=${encodeURIComponent(String(smoke.runId))}`}
              target="_blank"
              rel="noreferrer"
            >
              Open evidence bundle for runId (API)
            </a>
          ) : (
            <div className="text-gray-500">Run a smoke first to get a runId evidence link.</div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// GA-R2D: Scenario Seeds Panel (Admin-only test overlays)
// ============================================================================

type CalendarWeek = {
  week_number: number
  start_date: string
  end_date: string
  display: string
  fiscal_week: number
  fiscal_year: string
  quarter: string
  period: number
  nrf_month: string
}

type CalendarWeeksResponse = {
  weeks: CalendarWeek[]
  currentWeek: number
  currentFiscalYear: string
}

type ScenarioSeedItem = {
  id: string
  scenario_type: string
  calendar_week_key: string
  seed_payload: any
  expires_at: string
  created_by: string | null
  created_at: string
}

type ScenarioSeedListResponse = {
  ok: boolean
  items?: ScenarioSeedItem[]
  error?: string
}

type ScenarioSeedCreateResponse = {
  ok: boolean
  seedId?: string
  expiresAt?: string
  error?: string
}

function ScenarioSeedsPanel() {
  const [weeks, setWeeks] = useState<CalendarWeek[]>([])
  const [selectedRange, setSelectedRange] = useState<{ start: string; end: string } | null>(null)
  const [scenarioType, setScenarioType] = useState('execution_constraint')
  const [ttlMinutes, setTtlMinutes] = useState(120)
  const [seedItems, setSeedItems] = useState<ScenarioSeedItem[]>([])
  const [isLoadingWeeks, setIsLoadingWeeks] = useState(false)
  const [isLoadingSeeds, setIsLoadingSeeds] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const calendarWeekKey = selectedRange ? `${selectedRange.start}_to_${selectedRange.end}` : ''

  const loadWeeks = async () => {
    setIsLoadingWeeks(true)
    setError(null)
    const resp = await fetchJsonWithAuth<CalendarWeeksResponse>('/api/data/calendar-weeks', { method: 'GET' })
    if (!resp.ok) {
      setError(resp.text || 'Failed to load calendar weeks')
      setIsLoadingWeeks(false)
      return
    }
    setWeeks(resp.json.weeks)
    if (!selectedRange && resp.json.weeks.length > 0) {
      const latest = resp.json.weeks[0]
      setSelectedRange({ start: latest.start_date, end: latest.end_date })
    }
    setIsLoadingWeeks(false)
  }

  const loadSeeds = async () => {
    if (!calendarWeekKey) return
    setIsLoadingSeeds(true)
    setError(null)
    const resp = await fetchJsonWithAuth<ScenarioSeedListResponse>(
      `/api/admin/scenario-seeds/list?calendarWeekKey=${encodeURIComponent(calendarWeekKey)}`,
      { method: 'GET' }
    )
    if (!resp.ok) {
      setError(resp.text || 'Failed to load scenario seeds')
      setIsLoadingSeeds(false)
      return
    }
    setSeedItems(resp.json.items || [])
    setIsLoadingSeeds(false)
  }

  const createSeed = async () => {
    if (!calendarWeekKey) {
      setError('Select a calendar week')
      return
    }
    setIsCreating(true)
    setError(null)
    const resp = await fetchJsonWithAuth<ScenarioSeedCreateResponse>('/api/admin/scenario-seeds/create', {
      method: 'POST',
      body: {
        scenarioType,
        calendarWeekKey,
        ttlMinutes,
        seedPayload: {},
      },
    })
    if (!resp.ok) {
      setError(resp.text || 'Failed to create scenario seed')
      setIsCreating(false)
      return
    }
    if (!resp.json.ok) {
      setError(resp.json.error || 'Failed to create scenario seed')
      setIsCreating(false)
      return
    }
    await loadSeeds()
    setIsCreating(false)
  }

  const deleteSeed = async (seedId: string) => {
    setError(null)
    const resp = await fetchJsonWithAuth<{ ok: boolean; error?: string }>('/api/admin/scenario-seeds/delete', {
      method: 'POST',
      body: { seedId },
    })
    if (!resp.ok || !resp.json.ok) {
      setError(resp.ok ? resp.json.error || 'Failed to delete seed' : resp.text)
      return
    }
    await loadSeeds()
  }

  useEffect(() => {
    loadWeeks()
  }, [])

  useEffect(() => {
    loadSeeds()
  }, [calendarWeekKey])

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200">
        <div className="font-bold text-gray-900">
          Scenario Seeds <span className="text-xs font-normal text-gray-400">(GA-R2D)</span>
        </div>
        <div className="text-xs text-gray-500">
          Seeds are test overlays. They do not change KPI truth tables and expire automatically.
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <CalendarWeekPicker
            weeks={weeks}
            selectedRange={selectedRange}
            onSelectRange={(range) => setSelectedRange(range)}
          />
          <select
            value={scenarioType}
            onChange={(e) => setScenarioType(e.target.value)}
            className="border border-gray-200 rounded px-2 py-1 text-sm"
          >
            <option value="execution_constraint">Execution Constraint</option>
            <option value="forecast_miss">Forecast Miss</option>
          </select>
          <select
            value={ttlMinutes}
            onChange={(e) => setTtlMinutes(parseInt(e.target.value, 10))}
            className="border border-gray-200 rounded px-2 py-1 text-sm"
          >
            <option value={30}>30 min</option>
            <option value={60}>60 min</option>
            <option value={120}>120 min</option>
            <option value={240}>240 min</option>
          </select>
          <button
            onClick={createSeed}
            disabled={isCreating || isLoadingWeeks || !calendarWeekKey}
            className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60"
          >
            {isCreating ? 'Creating…' : 'Create Seed'}
          </button>
          <button
            onClick={loadSeeds}
            disabled={isLoadingSeeds || !calendarWeekKey}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-60"
          >
            {isLoadingSeeds ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error ? (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            {error}
          </div>
        ) : null}

        {isLoadingWeeks ? (
          <div className="text-sm text-gray-500">Loading calendar weeks…</div>
        ) : null}

        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Active seeds for {calendarWeekKey || '—'}
          </div>
          {seedItems.length === 0 ? (
            <div className="text-sm text-gray-500">No active seeds.</div>
          ) : (
            <div className="space-y-2">
              {seedItems.map((seed) => (
                <div key={seed.id} className="border border-gray-200 rounded-lg p-3 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">
                      {seed.scenario_type.replace(/_/g, ' ')}
                    </div>
                    <div className="text-xs text-gray-500">
                      Expires: {new Date(seed.expires_at).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-400">
                      Created by: {seed.created_by || 'service token'}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteSeed(seed.id)}
                    className="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-50"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// V3-3B: Outcome Evaluation Panel
// ============================================================================

type OutcomeEvalResponse = {
  success: boolean;
  processed: number;
  improved: number;
  neutral: number;
  worse: number;
  insufficient: number;
  errors?: string[];
}

function OutcomeEvaluationPanel({ companyId }: { companyId: string | null }) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<OutcomeEvalResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runEvaluation = async () => {
    if (!companyId) {
      setError('No company ID available');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const resp = await fetchJsonWithAuth<OutcomeEvalResponse>('/api/brain/outcomes/evaluate-due', {
        method: 'POST',
        body: JSON.stringify({ companyId }),
        timeoutMs: 30_000
      });

      if (!resp.ok) {
        setError(`HTTP ${resp.status}: ${resp.text || 'Failed'}`);
        return;
      }

      setResult(resp.json);
    } catch (e: any) {
      setError(e?.message || 'Failed to run evaluation');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">Outcome Evaluation <span className="text-xs font-normal text-gray-400">(V3-3B)</span></div>
          <div className="text-xs text-gray-500">Evaluate due actions and update confidence calibration</div>
        </div>
        <button
          onClick={runEvaluation}
          disabled={isLoading || !companyId}
          className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-60"
        >
          {isLoading ? 'Evaluating…' : 'Run Evaluation Now'}
        </button>
      </div>
      <div className="p-5 space-y-3">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            <div className="font-semibold">Evaluation error</div>
            <div className="mt-1">{error}</div>
          </div>
        )}
        
        {result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-900">{result.processed}</div>
                <div className="text-xs text-gray-500">Processed</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-emerald-700">{result.improved}</div>
                <div className="text-xs text-emerald-600">Improved</div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-600">{result.neutral}</div>
                <div className="text-xs text-gray-500">Neutral</div>
              </div>
              <div className="border border-red-200 bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-700">{result.worse}</div>
                <div className="text-xs text-red-600">Worse</div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-400">{result.insufficient}</div>
                <div className="text-xs text-gray-500">Insufficient Data</div>
              </div>
            </div>
            
            {result.errors && result.errors.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <div className="text-sm font-semibold text-amber-800 mb-1">Warnings</div>
                <ul className="text-xs text-amber-700 list-disc pl-4">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        
        {!result && !error && (
          <div className="text-sm text-gray-600">
            Click "Run Evaluation Now" to evaluate due actions and update confidence calibration for future recommendations.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// V3-3C: Learning Visibility Panel
// ============================================================================

type CalibrationEntry = { actionType: string; bias: number; n: number };
type ProvenPlayEntry = { actionType: string; successRate: number; totalOutcomes: number; improvedCount: number; isProven: boolean; avgDeltaPct: number | null };
type OutcomeRecord = { id: string; actionId: string; actionType: string; result: string; deltaMetric: string | null; deltaPct: number | null; confidenceAdjustment: number; evaluatedAt: string };

type CalibrationResponse = { ok: boolean; entries: CalibrationEntry[]; updatedAt: string | null; totalOutcomes: number };
type ProvenPlaysResponse = { ok: boolean; provenPlays: ProvenPlayEntry[]; summary: { provenCount: number; totalPlays: number; topPlay: string | null } };
type OutcomesResponse = { ok: boolean; outcomes: OutcomeRecord[]; count: number };
type BackdateResponse = { ok: boolean; actionId: string; oldCreatedAt: string; newCreatedAt: string; backdateDays: number; error?: string };

function LearningVisibilityPanel({ companyId }: { companyId: string | null }) {
  // Data state
  const [calibration, setCalibration] = useState<CalibrationResponse | null>(null);
  const [provenPlays, setProvenPlays] = useState<ProvenPlaysResponse | null>(null);
  const [outcomes, setOutcomes] = useState<OutcomesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Backdate form state
  const [backdateActionId, setBackdateActionId] = useState('');
  const [backdateDays, setBackdateDays] = useState(7);
  const [backdateReason, setBackdateReason] = useState('');
  const [backdateLoading, setBackdateLoading] = useState(false);
  const [backdateResult, setBackdateResult] = useState<BackdateResponse | null>(null);
  const [backdateError, setBackdateError] = useState<string | null>(null);

  const refreshLearning = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [calResp, provenResp, outcomesResp] = await Promise.all([
        fetchJsonWithAuth<CalibrationResponse>('/api/admin/learning/calibration', { method: 'GET', timeoutMs: 10_000 }),
        fetchJsonWithAuth<ProvenPlaysResponse>('/api/admin/learning/proven-plays', { method: 'GET', timeoutMs: 10_000 }),
        fetchJsonWithAuth<OutcomesResponse>('/api/admin/learning/outcomes?limit=20', { method: 'GET', timeoutMs: 10_000 }),
      ]);
      
      if (calResp.ok) setCalibration(calResp.json);
      if (provenResp.ok) setProvenPlays(provenResp.json);
      if (outcomesResp.ok) setOutcomes(outcomesResp.json);
      
      if (!calResp.ok && !provenResp.ok && !outcomesResp.ok) {
        setError('Failed to load learning data. Ensure ENABLE_ADMIN_HEALTH=true and admin access.');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load learning data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdate = async () => {
    if (!backdateActionId.trim()) {
      setBackdateError('Action ID is required');
      return;
    }
    if (!backdateReason.trim()) {
      setBackdateError('Reason is required');
      return;
    }
    
    setBackdateLoading(true);
    setBackdateError(null);
    setBackdateResult(null);
    
    try {
      const resp = await fetchJsonWithAuth<BackdateResponse>('/api/admin/actions/backdate', {
        method: 'POST',
        body: { actionId: backdateActionId.trim(), backdateDays, reason: backdateReason.trim() },
        timeoutMs: 15_000
      });
      
      if (!resp.ok) {
        setBackdateError(`HTTP ${resp.status}: ${resp.text || 'Failed'}`);
        return;
      }
      
      if (!resp.json.ok) {
        setBackdateError(resp.json.error || 'Backdate failed');
        return;
      }
      
      setBackdateResult(resp.json);
      setBackdateActionId('');
      setBackdateReason('');
    } catch (e: any) {
      setBackdateError(e?.message || 'Failed to backdate action');
    } finally {
      setBackdateLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">Learning & Calibration <span className="text-xs font-normal text-gray-400">(V3-3C)</span></div>
          <div className="text-xs text-gray-500">View outcome history, calibration biases, and proven plays</div>
        </div>
        <button
          onClick={refreshLearning}
          disabled={isLoading || !companyId}
          className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60"
        >
          {isLoading ? 'Loading…' : 'Refresh Learning'}
        </button>
      </div>
      
      <div className="p-5 space-y-6">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            <div className="font-semibold">Error</div>
            <div className="mt-1">{error}</div>
          </div>
        )}
        
        {/* Backdate Form */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-900 mb-3">Backdate Action (for evaluation harness)</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Action ID (UUID)"
              value={backdateActionId}
              onChange={(e) => setBackdateActionId(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
            <select
              value={backdateDays}
              onChange={(e) => setBackdateDays(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={45}>45 days</option>
              <option value={60}>60 days</option>
            </select>
            <input
              type="text"
              placeholder="Reason"
              value={backdateReason}
              onChange={(e) => setBackdateReason(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
            <button
              onClick={handleBackdate}
              disabled={backdateLoading || !companyId}
              className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm disabled:opacity-60"
            >
              {backdateLoading ? 'Backdating…' : 'Backdate'}
            </button>
          </div>
          {backdateError && (
            <div className="mt-2 text-xs text-red-600">{backdateError}</div>
          )}
          {backdateResult && (
            <div className="mt-2 text-xs text-green-600">
              ✓ Backdated action {backdateResult.actionId} by {backdateResult.backdateDays} days
            </div>
          )}
        </div>

        {/* Calibration Biases */}
        {calibration && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">
              Calibration Biases 
              <span className="text-xs font-normal text-gray-500 ml-2">
                ({calibration.totalOutcomes} total outcomes)
              </span>
            </div>
            {calibration.entries.length === 0 ? (
              <div className="text-xs text-gray-500">No calibration data yet. Run evaluations to build calibration.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1">Action Type</th>
                    <th className="py-1">Bias</th>
                    <th className="py-1">Sample Size</th>
                  </tr>
                </thead>
                <tbody>
                  {calibration.entries.map((e, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-1 font-mono text-xs">{e.actionType}</td>
                      <td className={`py-1 ${e.bias > 0 ? 'text-green-600' : e.bias < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        {e.bias > 0 ? '+' : ''}{(e.bias * 100).toFixed(1)}%
                      </td>
                      <td className="py-1 text-gray-600">{e.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Proven Plays */}
        {provenPlays && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">
              Proven Plays
              <span className="text-xs font-normal text-gray-500 ml-2">
                ({provenPlays.summary.provenCount} proven / {provenPlays.summary.totalPlays} total)
              </span>
            </div>
            {provenPlays.provenPlays.length === 0 ? (
              <div className="text-xs text-gray-500">No proven plays yet. Need ≥5 outcomes with ≥60% success rate.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1">Action Type</th>
                    <th className="py-1">Success Rate</th>
                    <th className="py-1">Outcomes</th>
                    <th className="py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {provenPlays.provenPlays.map((p, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-1 font-mono text-xs">{p.actionType}</td>
                      <td className="py-1 text-gray-600">{(p.successRate * 100).toFixed(0)}%</td>
                      <td className="py-1 text-gray-600">{p.totalOutcomes}</td>
                      <td className="py-1">
                        {p.isProven ? (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Proven</span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">Building</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Recent Outcomes */}
        {outcomes && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">
              Recent Outcomes
              <span className="text-xs font-normal text-gray-500 ml-2">
                (last {outcomes.count})
              </span>
            </div>
            {outcomes.outcomes.length === 0 ? (
              <div className="text-xs text-gray-500">No outcomes yet. Backdate actions and run evaluation.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500">
                      <th className="py-1">Action ID</th>
                      <th className="py-1">Type</th>
                      <th className="py-1">Result</th>
                      <th className="py-1">Delta</th>
                      <th className="py-1">Adjustment</th>
                      <th className="py-1">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outcomes.outcomes.map((o, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="py-1 font-mono text-xs truncate max-w-[100px]" title={o.actionId}>
                          {o.actionId.slice(0, 8)}…
                        </td>
                        <td className="py-1 text-xs">{o.actionType}</td>
                        <td className="py-1">
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            o.result === 'improved' ? 'bg-green-100 text-green-700' :
                            o.result === 'worse' ? 'bg-red-100 text-red-700' :
                            o.result === 'neutral' ? 'bg-gray-100 text-gray-600' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {o.result}
                          </span>
                        </td>
                        <td className="py-1 text-xs text-gray-600">
                          {o.deltaPct != null ? `${o.deltaPct > 0 ? '+' : ''}${o.deltaPct.toFixed(1)}%` : '—'}
                        </td>
                        <td className={`py-1 text-xs ${o.confidenceAdjustment > 0 ? 'text-green-600' : o.confidenceAdjustment < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {o.confidenceAdjustment > 0 ? '+' : ''}{(o.confidenceAdjustment * 100).toFixed(1)}%
                        </td>
                        <td className="py-1 text-xs text-gray-500">
                          {new Date(o.evaluatedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!calibration && !provenPlays && !outcomes && !error && (
          <div className="text-sm text-gray-600">
            Click "Refresh Learning" to view calibration data, proven plays, and recent outcomes.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// V3-5B: Brain Queue Health Panel
// ============================================================================

type QueueHealthResponse = {
  ok: boolean;
  last24h: {
    queued: number;
    running: number;
    done: number;
    failed: number;
  };
  oldestQueuedMinutes: number | null;
  recentFailures: Array<{
    queueId: string;
    trigger: string;
    calendarWeekKey: string;
    attempts: number;
    lastError: string | null;
    updatedAt: string;
  }>;
  error?: string;
};

type QueueProcessResponse = {
  ok: boolean;
  processed: number;
  done: number;
  failed: number;
  error?: string;
};

type QueueRetryResponse = {
  ok: boolean;
  queueId: string;
  status?: string;
  previousAttempts?: number;
  error?: string;
};

const MAX_RETRY_ATTEMPTS = 5;

function BrainQueueHealthPanel({ companyId }: { companyId: string | null }) {
  const [health, setHealth] = useState<QueueHealthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const refreshHealth = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url = companyId 
        ? `/api/admin/brain/queue-health?companyId=${encodeURIComponent(companyId)}`
        : '/api/admin/brain/queue-health';
      
      const resp = await fetchJsonWithAuth<QueueHealthResponse>(url, { method: 'GET', timeoutMs: 15000 });
      
      if (!resp.ok) {
        if (resp.status === 403) {
          setError('ENABLE_ADMIN_HEALTH is not enabled');
        } else {
          setError(`Failed to load queue health: ${resp.text}`);
        }
        return;
      }
      
      setHealth(resp.json);
    } catch (e: any) {
      setError(e?.message || 'Failed to load queue health');
    } finally {
      setIsLoading(false);
    }
  };

  const processQueue = async () => {
    setIsProcessing(true);
    try {
      const resp = await fetchJsonWithAuth<QueueProcessResponse>('/api/brain/queue/process', {
        method: 'POST',
        timeoutMs: 60000, // Brain runs can take a while
      });
      
      if (!resp.ok) {
        setError(`Process failed: ${resp.text}`);
        return;
      }
      
      // Refresh health after processing
      await refreshHealth();
    } catch (e: any) {
      setError(e?.message || 'Failed to process queue');
    } finally {
      setIsProcessing(false);
    }
  };

  const retryItem = async (queueId: string) => {
    setRetryingId(queueId);
    try {
      const resp = await fetchJsonWithAuth<QueueRetryResponse>('/api/admin/brain/queue-retry', {
        method: 'POST',
        body: { queueId },
        timeoutMs: 10000,
      });
      
      if (!resp.ok) {
        setError(`Retry failed: ${resp.text}`);
        return;
      }
      
      if (!resp.json.ok) {
        setError(`Retry failed: ${resp.json.error}`);
        return;
      }
      
      // Refresh health after retry
      await refreshHealth();
    } catch (e: any) {
      setError(e?.message || 'Failed to retry item');
    } finally {
      setRetryingId(null);
    }
  };

  // Initial load
  useEffect(() => {
    refreshHealth();
  }, [companyId]);

  const totalActive = (health?.last24h?.queued || 0) + (health?.last24h?.running || 0);
  const hasFailures = (health?.recentFailures?.length || 0) > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">
            Brain Queue Health <span className="text-xs font-normal text-gray-400">(V3-5B)</span>
          </div>
          <div className="text-xs text-gray-500">Auto-triggered BrainRuns: retail ingest, DTC anomaly, promo events</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshHealth}
            disabled={isLoading}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-60"
          >
            {isLoading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            onClick={processQueue}
            disabled={isProcessing || isLoading || totalActive === 0}
            className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm disabled:opacity-60"
          >
            {isProcessing ? 'Processing…' : 'Process Queue Now'}
          </button>
        </div>
      </div>
      
      <div className="p-5 space-y-4">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            {error}
          </div>
        )}

        {/* Summary Tiles */}
        {health && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-600">{health.last24h.queued}</div>
              <div className="text-xs text-gray-500">Queued (24h)</div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{health.last24h.running}</div>
              <div className="text-xs text-gray-500">Running</div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{health.last24h.done}</div>
              <div className="text-xs text-gray-500">Done (24h)</div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${health.last24h.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {health.last24h.failed}
              </div>
              <div className="text-xs text-gray-500">Failed</div>
            </div>
          </div>
        )}

        {/* Oldest Queued Age */}
        {health?.oldestQueuedMinutes != null && health.oldestQueuedMinutes > 0 && (
          <div className={`text-sm ${health.oldestQueuedMinutes > 30 ? 'text-red-600' : 'text-gray-600'}`}>
            ⏱ Oldest queued item: <strong>{health.oldestQueuedMinutes} minutes</strong> old
            {health.oldestQueuedMinutes > 30 && (
              <span className="ml-2 text-red-600">⚠️ Queue may be backing up</span>
            )}
          </div>
        )}

        {/* Failure Table */}
        {hasFailures && (
          <div className="border border-red-200 rounded-lg p-4">
            <div className="text-sm font-semibold text-red-800 mb-2">
              Recent Failures ({health?.recentFailures?.length})
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1">Trigger</th>
                    <th className="py-1">Week</th>
                    <th className="py-1">Attempts</th>
                    <th className="py-1">Error</th>
                    <th className="py-1">Updated</th>
                    <th className="py-1">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {health?.recentFailures?.map((f) => (
                    <tr key={f.queueId} className="border-t border-gray-100">
                      <td className="py-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          f.trigger === 'retail_ingest' ? 'bg-blue-100 text-blue-700' :
                          f.trigger === 'dtc_anomaly' ? 'bg-purple-100 text-purple-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {f.trigger}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-gray-600 font-mono">
                        {f.calendarWeekKey.split('_to_')[0]}
                      </td>
                      <td className="py-2 text-xs">
                        <span className={`${f.attempts >= MAX_RETRY_ATTEMPTS ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                          {f.attempts}/{MAX_RETRY_ATTEMPTS}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-red-600 max-w-[200px] truncate" title={f.lastError || ''}>
                        {f.lastError?.substring(0, 50) || '—'}
                        {(f.lastError?.length || 0) > 50 && '…'}
                      </td>
                      <td className="py-2 text-xs text-gray-500">
                        {new Date(f.updatedAt).toLocaleString()}
                      </td>
                      <td className="py-2">
                        <button
                          onClick={() => retryItem(f.queueId)}
                          disabled={retryingId === f.queueId || f.attempts >= MAX_RETRY_ATTEMPTS}
                          className={`px-2 py-1 text-xs rounded ${
                            f.attempts >= MAX_RETRY_ATTEMPTS
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                          title={f.attempts >= MAX_RETRY_ATTEMPTS ? 'Max attempts reached' : 'Retry this item'}
                        >
                          {retryingId === f.queueId ? '…' : 'Retry'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!health && !isLoading && !error && (
          <div className="text-sm text-gray-600">
            Click "Refresh" to view Brain Queue health.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PIQ-V1: Case Gating Thresholds Panel
// ============================================================================

type CaseGatingConfig = {
  minUnexpectedLossDollars: number
  minUnexpectedLossPct: number
  exceptionMaterialityPct: number
  updatedAt: string
}

type CaseGatingConfigResponse = {
  ok: boolean
  config?: CaseGatingConfig
  defaults?: CaseGatingConfig
  error?: string
}

function CaseGatingThresholdsPanel({ companyId }: { companyId: string | null }) {
  const [config, setConfig] = useState<CaseGatingConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  const loadConfig = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const resp = await fetchJsonWithAuth<CaseGatingConfigResponse>('/api/admin/case-gating-config', {
        method: 'GET',
        timeoutMs: 10_000
      })
      if (!resp.ok) {
        setError(resp.status === 403 ? 'ENABLE_ADMIN_HEALTH is not enabled' : 'Failed to load config')
        return
      }
      setConfig(resp.json.config || null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load config')
    } finally {
      setIsLoading(false)
    }
  }

  const saveConfig = async () => {
    if (!config) return
    setIsSaving(true)
    setError(null)
    setSavedMessage(null)
    try {
      const resp = await fetchJsonWithAuth<CaseGatingConfigResponse>('/api/admin/case-gating-config', {
        method: 'POST',
        body: config,
        timeoutMs: 10_000
      })
      if (!resp.ok) {
        setError(resp.status === 403 ? 'ENABLE_ADMIN_HEALTH is not enabled' : 'Failed to save config')
        return
      }
      setConfig(resp.json.config || config)
      setSavedMessage('Configuration saved successfully')
      setTimeout(() => setSavedMessage(null), 3000)
    } catch (e: any) {
      setError(e?.message || 'Failed to save config')
    } finally {
      setIsSaving(false)
    }
  }

  const resetDefaults = () => {
    setConfig({
      minUnexpectedLossDollars: 1000,
      minUnexpectedLossPct: 0.10,
      exceptionMaterialityPct: 0.15,
      updatedAt: new Date().toISOString()
    })
  }

  useEffect(() => {
    loadConfig()
  }, [])

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">
            Case Gating Thresholds <span className="text-xs font-normal text-gray-400">(PIQ-V1)</span>
          </div>
          <div className="text-xs text-gray-500">
            CEV (Counterfactual Expected Value) thresholds for case creation. Cases require unexpected loss exceeding these thresholds.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetDefaults}
            disabled={isLoading || isSaving}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-60"
          >
            Reset Defaults
          </button>
          <button
            onClick={saveConfig}
            disabled={isLoading || isSaving || !config}
            className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            <div className="font-semibold">Error</div>
            <div className="mt-1">{error}</div>
          </div>
        )}

        {savedMessage && (
          <div className="border border-green-200 bg-green-50 text-green-800 rounded-lg p-3 text-sm">
            {savedMessage}
          </div>
        )}

        {isLoading && !config ? (
          <div className="text-sm text-gray-500">Loading case gating config...</div>
        ) : null}

        {config ? (
          <div className="space-y-6">
            {/* Min Unexpected Loss Dollars */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-900">
                  Min Unexpected Loss ($)
                </label>
                <span className="text-sm text-gray-600 font-mono">
                  ${config.minUnexpectedLossDollars.toLocaleString()}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={10000}
                step={100}
                value={config.minUnexpectedLossDollars}
                onChange={(e) => setConfig({
                  ...config,
                  minUnexpectedLossDollars: parseInt(e.target.value, 10)
                })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>$0</span>
                <span>$5,000</span>
                <span>$10,000</span>
              </div>
              <p className="text-xs text-gray-500">
                Minimum absolute dollar value of unexpected loss to create a case.
              </p>
            </div>

            {/* Min Unexpected Loss Pct */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-900">
                  Min Unexpected Loss (%)
                </label>
                <span className="text-sm text-gray-600 font-mono">
                  {(config.minUnexpectedLossPct * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={0.5}
                step={0.01}
                value={config.minUnexpectedLossPct}
                onChange={(e) => setConfig({
                  ...config,
                  minUnexpectedLossPct: parseFloat(e.target.value)
                })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
              </div>
              <p className="text-xs text-gray-500">
                Minimum percentage deviation from expected range to create a case.
              </p>
            </div>

            {/* Exception Materiality Pct */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-900">
                  Exception Materiality (%)
                </label>
                <span className="text-sm text-gray-600 font-mono">
                  {(config.exceptionMaterialityPct * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min={0.05}
                max={0.3}
                step={0.01}
                value={config.exceptionMaterialityPct}
                onChange={(e) => setConfig({
                  ...config,
                  exceptionMaterialityPct: parseFloat(e.target.value)
                })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>5%</span>
                <span>17.5%</span>
                <span>30%</span>
              </div>
              <p className="text-xs text-gray-500">
                Allows case creation even if loss thresholds not met, if any single metric exceeds this materiality level.
              </p>
            </div>

            {/* Last Updated */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                Last updated: {new Date(config.updatedAt).toLocaleString()}
              </div>
            </div>
          </div>
        ) : null}

        {!config && !isLoading && !error && (
          <div className="text-sm text-gray-600">
            Click to load case gating configuration.
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// PIQ-V1: Belief Accuracy Panel
// ============================================================================

type BeliefAccuracyResponse = {
  ok: boolean
  byBelief?: Record<string, { 
    w13?: { accuracy: number; n: number; correctCount: number; incorrectCount: number; uncertainCount: number }
    w26?: { accuracy: number; n: number; correctCount: number; incorrectCount: number; uncertainCount: number }
  }>
  topWrongWeeks?: Array<{
    calendarWeekKey: string
    beliefPrimary: string
    beliefConfidence: number
    outcome: string
    score: number
    reasonCodes: string[]
  }>
  lowAccuracyWarnings?: Array<{
    beliefId: string
    accuracy: number
    n: number
    suggestion: string
  }>
  summary?: {
    totalEvaluations: number
    overallAccuracy13w: number | null
    hasLowAccuracyBeliefs: boolean
    beliefCount: number
  }
  error?: string
}

function BeliefAccuracyPanel({ companyId }: { companyId: string | null }) {
  const [data, setData] = useState<BeliefAccuracyResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isRecomputing, setIsRecomputing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const resp = await fetchJsonWithAuth<BeliefAccuracyResponse>('/api/admin/belief-accuracy', {
        method: 'GET',
        timeoutMs: 15_000
      })
      if (!resp.ok) {
        setError(resp.status === 403 ? 'ENABLE_ADMIN_HEALTH is not enabled' : 'Failed to load data')
        return
      }
      setData(resp.json)
    } catch (e: any) {
      setError(e?.message || 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }

  const recomputeRollups = async () => {
    setIsRecomputing(true)
    setError(null)
    try {
      const resp = await fetchJsonWithAuth<BeliefAccuracyResponse>('/api/admin/belief-accuracy', {
        method: 'POST',
        timeoutMs: 30_000
      })
      if (!resp.ok) {
        setError('Failed to recompute rollups')
        return
      }
      // Reload data after recompute
      await loadData()
    } catch (e: any) {
      setError(e?.message || 'Failed to recompute')
    } finally {
      setIsRecomputing(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const formatAccuracy = (accuracy: number) => `${Math.round(accuracy * 100)}%`
  
  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 0.7) return 'text-green-600'
    if (accuracy >= 0.5) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">
            Belief Accuracy <span className="text-xs font-normal text-gray-400">(PIQ-V1 BAM)</span>
          </div>
          <div className="text-xs text-gray-500">
            Historical accuracy of belief predictions vs actual outcomes
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            disabled={isLoading}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-60"
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            onClick={recomputeRollups}
            disabled={isRecomputing || isLoading}
            className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60"
          >
            {isRecomputing ? 'Recomputing...' : 'Recompute'}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            <div className="font-semibold">Error</div>
            <div className="mt-1">{error}</div>
          </div>
        )}

        {isLoading && !data ? (
          <div className="text-sm text-gray-500">Loading belief accuracy data...</div>
        ) : null}

        {data ? (
          <div className="space-y-6">
            {/* Summary */}
            {data.summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-gray-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {data.summary.totalEvaluations}
                  </div>
                  <div className="text-xs text-gray-500">Evaluations</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${data.summary.overallAccuracy13w ? getAccuracyColor(data.summary.overallAccuracy13w) : 'text-gray-400'}`}>
                    {data.summary.overallAccuracy13w ? formatAccuracy(data.summary.overallAccuracy13w) : 'n/a'}
                  </div>
                  <div className="text-xs text-gray-500">13w Accuracy</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {data.summary.beliefCount}
                  </div>
                  <div className="text-xs text-gray-500">Beliefs Tracked</div>
                </div>
                <div className="border border-gray-200 rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${data.summary.hasLowAccuracyBeliefs ? 'text-amber-600' : 'text-green-600'}`}>
                    {data.summary.hasLowAccuracyBeliefs ? 'Warning' : 'OK'}
                  </div>
                  <div className="text-xs text-gray-500">Health</div>
                </div>
              </div>
            )}

            {/* Low Accuracy Warnings */}
            {data.lowAccuracyWarnings && data.lowAccuracyWarnings.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                <div className="text-sm font-semibold text-amber-800 mb-2">
                  Low Accuracy Warnings
                </div>
                <div className="space-y-2">
                  {data.lowAccuracyWarnings.map((warning, i) => (
                    <div key={i} className="text-sm text-amber-700">
                      <span className="font-medium">{warning.beliefId.replace(/_/g, ' ')}</span>
                      : {formatAccuracy(warning.accuracy)} accuracy ({warning.n} samples)
                      <div className="text-xs text-amber-600 mt-1">{warning.suggestion}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Accuracy by Belief Table */}
            {data.byBelief && Object.keys(data.byBelief).length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-gray-900 mb-3">Accuracy by Belief</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500">
                      <th className="py-1">Belief</th>
                      <th className="py-1 text-center">13w Accuracy</th>
                      <th className="py-1 text-center">13w n</th>
                      <th className="py-1 text-center">26w Accuracy</th>
                      <th className="py-1 text-center">26w n</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.byBelief).map(([beliefId, metrics]) => (
                      <tr key={beliefId} className="border-t border-gray-100">
                        <td className="py-2 text-xs">{beliefId.replace(/_/g, ' ')}</td>
                        <td className={`py-2 text-center ${metrics.w13 ? getAccuracyColor(metrics.w13.accuracy) : 'text-gray-400'}`}>
                          {metrics.w13 ? formatAccuracy(metrics.w13.accuracy) : 'n/a'}
                        </td>
                        <td className="py-2 text-center text-gray-600">
                          {metrics.w13?.n || 0}
                        </td>
                        <td className={`py-2 text-center ${metrics.w26 ? getAccuracyColor(metrics.w26.accuracy) : 'text-gray-400'}`}>
                          {metrics.w26 ? formatAccuracy(metrics.w26.accuracy) : 'n/a'}
                        </td>
                        <td className="py-2 text-center text-gray-600">
                          {metrics.w26?.n || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Top Wrong Weeks */}
            {data.topWrongWeeks && data.topWrongWeeks.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-gray-900 mb-3">Recent Wrong Predictions</div>
                <div className="space-y-2">
                  {data.topWrongWeeks.map((week, i) => (
                    <div key={i} className="text-sm border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-700">
                          {week.beliefPrimary.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500">{week.calendarWeekKey.split('_to_')[0]}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Confidence: {Math.round(week.beliefConfidence * 100)}% | 
                        Reasons: {week.reasonCodes.join(', ') || 'none'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {!data && !isLoading && !error && (
          <div className="text-sm text-gray-600">
            Click Refresh to load belief accuracy data.
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// PIQ-V2: Weekly Actuals Backfill Panel
// ============================================================================

type BackfillResponse = {
  ok: boolean
  weeksProcessed: number
  rowsUpsertedRetail: number
  rowsUpsertedDTC: number
  errors: string[]
  weeksWithData: string[]
  weeksWithoutData: string[]
  durationMs: number
  dryRun: boolean
  jobId?: string
}

function WeeklyActualsBackfillPanel({ companyId }: { companyId: string | null }) {
  const [isRunning, setIsRunning] = useState(false)
  const [isDryRun, setIsDryRun] = useState(true)
  const [limitWeeks, setLimitWeeks] = useState(12)
  const [result, setResult] = useState<BackfillResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const runBackfill = async () => {
    setIsRunning(true)
    setError(null)
    setResult(null)
    try {
      const resp = await fetchJsonWithAuth<BackfillResponse>('/api/admin/v2/backfill/weekly-actuals', {
        method: 'POST',
        body: {
          limitWeeks,
          dryRun: isDryRun,
          companyId: companyId || undefined
        },
        timeoutMs: 120_000  // 2 minutes for large backfills
      })
      if (!resp.ok) {
        setError(resp.status === 403 ? 'ENABLE_ADMIN_HEALTH is not enabled' : `Failed: ${resp.text}`)
        return
      }
      setResult(resp.json)
    } catch (e: any) {
      setError(e?.message || 'Failed to run backfill')
    } finally {
      setIsRunning(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">
            Weekly Actuals Backfill <span className="text-xs font-normal text-gray-400">(PIQ-V2)</span>
          </div>
          <div className="text-xs text-gray-500">
            Backfill weekly actuals snapshots from retail KPI + DTC tables for historical CEV evaluation
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isDryRun}
              onChange={(e) => setIsDryRun(e.target.checked)}
              className="rounded"
            />
            Dry Run
          </label>
          <select
            value={limitWeeks}
            onChange={(e) => setLimitWeeks(parseInt(e.target.value, 10))}
            className="border border-gray-200 rounded px-2 py-1 text-sm"
          >
            <option value={4}>4 weeks</option>
            <option value={12}>12 weeks</option>
            <option value={26}>26 weeks</option>
            <option value={52}>52 weeks</option>
          </select>
          <button
            onClick={runBackfill}
            disabled={isRunning}
            className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm disabled:opacity-60"
          >
            {isRunning ? 'Running...' : isDryRun ? 'Preview Backfill' : 'Run Backfill'}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            <div className="font-semibold">Error</div>
            <div className="mt-1">{error}</div>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`border rounded-lg p-4 ${result.dryRun ? 'border-blue-200 bg-blue-50' : result.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-sm font-semibold ${result.dryRun ? 'text-blue-800' : result.ok ? 'text-green-800' : 'text-red-800'}`}>
                  {result.dryRun ? 'DRY RUN Preview' : result.ok ? 'Backfill Complete' : 'Backfill Failed'}
                </div>
                <div className="text-xs text-gray-500">
                  {(result.durationMs / 1000).toFixed(1)}s
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-900">{result.weeksProcessed}</div>
                  <div className="text-xs text-gray-500">Weeks Processed</div>
                </div>
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{result.rowsUpsertedRetail}</div>
                  <div className="text-xs text-gray-500">Retail Rows</div>
                </div>
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-600">{result.rowsUpsertedDTC}</div>
                  <div className="text-xs text-gray-500">DTC Rows</div>
                </div>
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${result.weeksWithData.length > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {result.weeksWithData.length}
                  </div>
                  <div className="text-xs text-gray-500">Weeks w/ Data</div>
                </div>
              </div>
            </div>

            {/* Weeks with Data */}
            {result.weeksWithData.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  Weeks with Data ({result.weeksWithData.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.weeksWithData.slice(0, 12).map((week, i) => (
                    <span key={i} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                      {week.split('_to_')[0]}
                    </span>
                  ))}
                  {result.weeksWithData.length > 12 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      +{result.weeksWithData.length - 12} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Weeks without Data */}
            {result.weeksWithoutData.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  Weeks without Data ({result.weeksWithoutData.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.weeksWithoutData.slice(0, 8).map((week, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      {week.split('_to_')[0]}
                    </span>
                  ))}
                  {result.weeksWithoutData.length > 8 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      +{result.weeksWithoutData.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Errors */}
            {result.errors.length > 0 && (
              <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                <div className="text-sm font-semibold text-red-800 mb-2">
                  Errors ({result.errors.length})
                </div>
                <ul className="text-xs text-red-700 list-disc pl-4 space-y-1">
                  {result.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {result.errors.length > 5 && (
                    <li>+{result.errors.length - 5} more errors</li>
                  )}
                </ul>
              </div>
            )}

            {/* Job ID */}
            {result.jobId && (
              <div className="text-xs text-gray-500">
                Job ID: <code className="px-1 py-0.5 bg-gray-100 rounded">{result.jobId}</code>
              </div>
            )}
          </div>
        )}

        {!result && !error && (
          <div className="text-sm text-gray-600">
            <p>Backfills weekly actuals snapshots for CEV (Counterfactual Expected Value) evaluation.</p>
            <ul className="mt-2 text-xs text-gray-500 list-disc pl-4 space-y-1">
              <li><strong>Retail:</strong> Aggregates from sku_detail_current per retailer (Ulta, Macy's, JCP)</li>
              <li><strong>DTC:</strong> Aggregates from dtc_sales_summary per channel (Shopify, Amazon, Walmart)</li>
              <li><strong>Dry Run:</strong> Preview what would be written without persisting</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// PIQ-V2.8: Snapshot Completeness Panel
// ============================================================================

type SnapshotCompletenessResponse = {
  ok: boolean
  mode: 'single' | 'multi'
  companyId: string
  calendarWeekKey?: string
  retail?: {
    required: string[]
    present: string[]
    missing: string[]
    hasAnyData: boolean
    rows: Array<{ retailer: string; revenue: number | null; units: number | null }>
  }
  dtc?: {
    required: string[]
    present: string[]
    missing: string[]
    hasAnyData: boolean
    rows: Array<{ channel: string; revenue: number | null; orders: number | null }>
  }
  hasAnyData?: boolean
  isComplete?: boolean
  summary?: {
    totalWeeks: number
    completeWeeks: number
    incompleteWeeks: number
    weeksWithNoData: number
    commonMissingRetail: string[]
    commonMissingDtc: string[]
  }
  byWeek?: Array<{
    calendarWeekKey: string
    retail: { missing: string[]; present: string[]; hasAnyData: boolean }
    dtc: { missing: string[]; present: string[]; hasAnyData: boolean }
    isComplete: boolean
    hasAnyData: boolean
  }>
  error?: string
}

function SnapshotCompletenessPanel({ companyId }: { companyId: string | null }) {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedWeek, setSelectedWeek] = useState<string>('')
  const [limitWeeks, setLimitWeeks] = useState(12)
  const [result, setResult] = useState<SnapshotCompletenessResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const checkCompleteness = async (weekKey?: string) => {
    setIsLoading(true)
    setError(null)
    setResult(null)
    try {
      const params = new URLSearchParams()
      if (weekKey) params.set('calendarWeekKey', weekKey)
      else params.set('limitWeeks', String(limitWeeks))
      if (companyId) params.set('companyId', companyId)

      const resp = await fetchJsonWithAuth<SnapshotCompletenessResponse>(
        `/api/admin/v2/snapshots/completeness?${params.toString()}`,
        { method: 'GET', timeoutMs: 30_000 }
      )
      if (!resp.ok) {
        setError(resp.status === 403 ? 'ENABLE_ADMIN_HEALTH is not enabled' : `Failed: ${resp.text}`)
        return
      }
      setResult(resp.json)
    } catch (e: any) {
      setError(e?.message || 'Failed to check completeness')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">
            Snapshot Completeness <span className="text-xs font-normal text-gray-400">(V2.8)</span>
          </div>
          <div className="text-xs text-gray-500">
            Check if weekly snapshots have all required retailer/channel rows
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="2026-01-13_to_2026-01-19"
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            className="border border-gray-200 rounded px-2 py-1 text-sm w-48"
          />
          <select
            value={limitWeeks}
            onChange={(e) => setLimitWeeks(parseInt(e.target.value, 10))}
            className="border border-gray-200 rounded px-2 py-1 text-sm"
            title="Weeks to check if no specific week"
          >
            <option value={12}>12 weeks</option>
            <option value={26}>26 weeks</option>
            <option value={52}>52 weeks</option>
          </select>
          <button
            onClick={() => checkCompleteness(selectedWeek || undefined)}
            disabled={isLoading}
            className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm disabled:opacity-60"
          >
            {isLoading ? 'Checking...' : selectedWeek ? 'Check Week' : 'Check Recent'}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            <div className="font-semibold">Error</div>
            <div className="mt-1">{error}</div>
          </div>
        )}

        {/* Single Week Result */}
        {result && result.mode === 'single' && (
          <div className="space-y-4">
            <div className={`border rounded-lg p-4 ${result.isComplete ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`text-sm font-semibold ${result.isComplete ? 'text-green-800' : 'text-yellow-800'}`}>
                  {result.calendarWeekKey} — {result.isComplete ? 'Complete' : 'Incomplete'}
                </div>
                <StatusPill ok={result.isComplete ?? false} label={result.isComplete ? 'PASS' : 'GAPS'} />
              </div>

              {/* Retail */}
              <div className="mt-3">
                <div className="text-xs font-semibold text-gray-700 mb-1">Retail Channels</div>
                <div className="flex flex-wrap gap-2">
                  {result.retail?.required.map((r) => {
                    const isPresent = result.retail?.present.includes(r)
                    const row = result.retail?.rows.find(row => row.retailer === r)
                    return (
                      <span
                        key={r}
                        className={`px-2 py-1 text-xs rounded ${isPresent ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                        title={isPresent && row?.revenue ? `$${row.revenue.toLocaleString()}` : 'Missing'}
                      >
                        {r} {isPresent ? '✓' : '✗'}
                      </span>
                    )
                  })}
                </div>
              </div>

              {/* DTC */}
              <div className="mt-3">
                <div className="text-xs font-semibold text-gray-700 mb-1">DTC Channels</div>
                <div className="flex flex-wrap gap-2">
                  {result.dtc?.required.map((c) => {
                    const isPresent = result.dtc?.present.includes(c)
                    const row = result.dtc?.rows.find(row => row.channel === c)
                    return (
                      <span
                        key={c}
                        className={`px-2 py-1 text-xs rounded ${isPresent ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                        title={isPresent && row?.revenue ? `$${row.revenue.toLocaleString()}` : 'Missing'}
                      >
                        {c} {isPresent ? '✓' : '✗'}
                      </span>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Multi Week Result */}
        {result && result.mode === 'multi' && result.summary && (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-900">{result.summary.totalWeeks}</div>
                <div className="text-xs text-gray-500">Total Weeks</div>
              </div>
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{result.summary.completeWeeks}</div>
                <div className="text-xs text-gray-500">Complete</div>
              </div>
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">{result.summary.incompleteWeeks}</div>
                <div className="text-xs text-gray-500">Incomplete</div>
              </div>
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{result.summary.weeksWithNoData}</div>
                <div className="text-xs text-gray-500">No Data</div>
              </div>
            </div>

            {/* Common Missing */}
            {(result.summary.commonMissingRetail.length > 0 || result.summary.commonMissingDtc.length > 0) && (
              <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                <div className="text-sm font-semibold text-yellow-800 mb-2">Commonly Missing (&gt;50% of weeks)</div>
                {result.summary.commonMissingRetail.length > 0 && (
                  <div className="text-xs text-yellow-700">
                    <strong>Retail:</strong> {result.summary.commonMissingRetail.join(', ')}
                  </div>
                )}
                {result.summary.commonMissingDtc.length > 0 && (
                  <div className="text-xs text-yellow-700 mt-1">
                    <strong>DTC:</strong> {result.summary.commonMissingDtc.join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* Per-Week Table */}
            {result.byWeek && result.byWeek.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="text-xs font-semibold text-gray-700 px-3 py-2 bg-gray-50 border-b border-gray-200">
                  Per-Week Status
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 text-gray-500">Week</th>
                        <th className="text-center py-2 px-3 text-gray-500">Retail</th>
                        <th className="text-center py-2 px-3 text-gray-500">DTC</th>
                        <th className="text-center py-2 px-3 text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.byWeek.slice(0, 20).map((w, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 px-3 font-mono">{w.calendarWeekKey.split('_to_')[0]}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`px-1.5 py-0.5 rounded ${w.retail.hasAnyData ? (w.retail.missing.length === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700') : 'bg-red-100 text-red-700'}`}>
                              {w.retail.present.length}/{w.retail.present.length + w.retail.missing.length}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`px-1.5 py-0.5 rounded ${w.dtc.hasAnyData ? (w.dtc.missing.length === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700') : 'bg-red-100 text-red-700'}`}>
                              {w.dtc.present.length}/{w.dtc.present.length + w.dtc.missing.length}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            {w.isComplete ? (
                              <span className="text-green-600">✓</span>
                            ) : w.hasAnyData ? (
                              <span className="text-yellow-600">◐</span>
                            ) : (
                              <span className="text-red-600">✗</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {!result && !error && (
          <div className="text-sm text-gray-600">
            <p>Validates snapshot table completeness for the Retail Calendar Narrative feature.</p>
            <ul className="mt-2 text-xs text-gray-500 list-disc pl-4 space-y-1">
              <li><strong>Retail Required:</strong> total, ulta, macys, jcp</li>
              <li><strong>DTC Required:</strong> dtc_total, shopify, amazon</li>
              <li>Enter a specific week or leave blank to check recent weeks</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// PIQ-V2 Phase 3: Belief Accuracy Replay Panel
// ============================================================================

type BeliefAccuracyReplayResponse = {
  ok: boolean
  weeksProcessed: number
  weeksEvaluated: number
  weeksFailed: number
  accuracyStats: {
    correct: number
    incorrect: number
    uncertain: number
    total: number
    accuracyPct: number
  }
  results?: Array<{
    calendarWeekKey: string
    beliefPrimary: string | null
    outcome: string | null
    score: number | null
    hasWeekWData: boolean
    hasWeekW1Data: boolean
    reasonCodes: string[]
    error?: string
  }>
  dryRun: boolean
  durationMs: number
  error?: string
}

type BeliefAccuracyRollupsResponse = {
  ok: boolean
  companyId: string
  totalEvaluations: number
  overallAccuracy: {
    correct: number
    incorrect: number
    uncertain: number
    total: number
    accuracyPct: number
    avgScore: number
  }
  byBeliefType: Array<{
    beliefPrimary: string
    correct: number
    incorrect: number
    uncertain: number
    total: number
    accuracyPct: number
    avgScore: number
  }>
  weeklyTrend: Array<{
    calendarWeekKey: string
    correct: number
    incorrect: number
    uncertain: number
    total: number
    accuracyPct: number
  }>
  topPerformers: Array<{ beliefPrimary: string; accuracyPct: number }>
  underPerformers: Array<{ beliefPrimary: string; accuracyPct: number }>
  durationMs: number
  error?: string
}

function BeliefAccuracyReplayPanel({ companyId }: { companyId: string | null }) {
  const [isReplayRunning, setIsReplayRunning] = useState(false)
  const [isRollupsLoading, setIsRollupsLoading] = useState(false)
  const [isDryRun, setIsDryRun] = useState(true)
  const [limitWeeks, setLimitWeeks] = useState(26)
  const [replayResult, setReplayResult] = useState<BeliefAccuracyReplayResponse | null>(null)
  const [rollups, setRollups] = useState<BeliefAccuracyRollupsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Load rollups on mount
  useEffect(() => {
    loadRollups()
  }, [companyId])

  const loadRollups = async () => {
    setIsRollupsLoading(true)
    setError(null)
    try {
      const resp = await fetchJsonWithAuth<BeliefAccuracyRollupsResponse>(
        `/api/admin/v2/replay/belief-accuracy-rollups?companyId=${companyId || ''}`,
        { method: 'GET', timeoutMs: 30_000 }
      )
      if (!resp.ok) {
        if (resp.status === 403) {
          setError('ENABLE_ADMIN_HEALTH is not enabled')
        }
        return
      }
      setRollups(resp.json)
    } catch (e: any) {
      console.error('[BeliefAccuracyPanel] Failed to load rollups:', e)
    } finally {
      setIsRollupsLoading(false)
    }
  }

  const runReplay = async () => {
    setIsReplayRunning(true)
    setError(null)
    setReplayResult(null)
    try {
      const resp = await fetchJsonWithAuth<BeliefAccuracyReplayResponse>('/api/admin/v2/replay/belief-accuracy', {
        method: 'POST',
        body: {
          limitWeeks,
          dryRun: isDryRun,
          companyId: companyId || undefined
        },
        timeoutMs: 180_000  // 3 minutes for large replays
      })
      if (!resp.ok) {
        setError(resp.status === 403 ? 'ENABLE_ADMIN_HEALTH is not enabled' : `Failed: ${resp.text}`)
        return
      }
      setReplayResult(resp.json)
      // Refresh rollups after replay
      if (!isDryRun) {
        await loadRollups()
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to run replay')
    } finally {
      setIsReplayRunning(false)
    }
  }

  const outcomeColors: Record<string, string> = {
    correct: 'text-green-700 bg-green-100',
    incorrect: 'text-red-700 bg-red-100',
    uncertain: 'text-gray-700 bg-gray-100',
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="font-bold text-gray-900">
            Belief Accuracy Replay <span className="text-xs font-normal text-gray-400">(PIQ-V2 Phase 3)</span>
          </div>
          <div className="text-xs text-gray-500">
            Retrospective belief accuracy evaluation using snapshot tables
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isDryRun}
              onChange={(e) => setIsDryRun(e.target.checked)}
              className="rounded"
            />
            Dry Run
          </label>
          <select
            value={limitWeeks}
            onChange={(e) => setLimitWeeks(parseInt(e.target.value, 10))}
            className="border border-gray-200 rounded px-2 py-1 text-sm"
          >
            <option value={12}>12 weeks</option>
            <option value={26}>26 weeks</option>
            <option value={52}>52 weeks</option>
          </select>
          <button
            onClick={runReplay}
            disabled={isReplayRunning}
            className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm disabled:opacity-60"
          >
            {isReplayRunning ? 'Running...' : isDryRun ? 'Preview Replay' : 'Run Replay'}
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
            <div className="font-semibold">Error</div>
            <div className="mt-1">{error}</div>
          </div>
        )}

        {/* Current Rollups Summary */}
        {rollups && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-900">Current Accuracy Rollups</div>
              <button
                onClick={loadRollups}
                disabled={isRollupsLoading}
                className="text-xs text-violet-600 hover:underline"
              >
                {isRollupsLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-900">{rollups.totalEvaluations}</div>
                <div className="text-xs text-gray-500">Total Evaluations</div>
              </div>
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{rollups.overallAccuracy.correct}</div>
                <div className="text-xs text-gray-500">Correct</div>
              </div>
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{rollups.overallAccuracy.incorrect}</div>
                <div className="text-xs text-gray-500">Incorrect</div>
              </div>
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-600">{rollups.overallAccuracy.uncertain}</div>
                <div className="text-xs text-gray-500">Uncertain</div>
              </div>
              <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                <div className={`text-2xl font-bold ${rollups.overallAccuracy.accuracyPct >= 70 ? 'text-green-600' : rollups.overallAccuracy.accuracyPct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {rollups.overallAccuracy.accuracyPct}%
                </div>
                <div className="text-xs text-gray-500">Accuracy</div>
              </div>
            </div>

            {/* By Belief Type */}
            {rollups.byBeliefType.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-gray-700 mb-2">Accuracy by Belief Type</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-2 text-gray-500">Belief</th>
                        <th className="text-center py-2 px-2 text-gray-500">Total</th>
                        <th className="text-center py-2 px-2 text-green-600">Correct</th>
                        <th className="text-center py-2 px-2 text-red-600">Wrong</th>
                        <th className="text-center py-2 px-2 text-gray-500">Accuracy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rollups.byBeliefType.map((bt, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 px-2 font-medium">{bt.beliefPrimary?.replace(/_/g, ' ')}</td>
                          <td className="py-2 px-2 text-center">{bt.total}</td>
                          <td className="py-2 px-2 text-center text-green-600">{bt.correct}</td>
                          <td className="py-2 px-2 text-center text-red-600">{bt.incorrect}</td>
                          <td className="py-2 px-2 text-center">
                            <span className={`px-2 py-0.5 rounded ${bt.accuracyPct >= 70 ? 'bg-green-100 text-green-700' : bt.accuracyPct >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                              {bt.accuracyPct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Replay Result */}
        {replayResult && (
          <div className="space-y-4">
            <div className={`border rounded-lg p-4 ${replayResult.dryRun ? 'border-blue-200 bg-blue-50' : replayResult.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-sm font-semibold ${replayResult.dryRun ? 'text-blue-800' : replayResult.ok ? 'text-green-800' : 'text-red-800'}`}>
                  {replayResult.dryRun ? 'DRY RUN Preview' : replayResult.ok ? 'Replay Complete' : 'Replay Failed'}
                </div>
                <div className="text-xs text-gray-500">
                  {(replayResult.durationMs / 1000).toFixed(1)}s
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-900">{replayResult.weeksProcessed}</div>
                  <div className="text-xs text-gray-500">Weeks Found</div>
                </div>
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{replayResult.weeksEvaluated}</div>
                  <div className="text-xs text-gray-500">Evaluated</div>
                </div>
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{replayResult.accuracyStats.correct}</div>
                  <div className="text-xs text-gray-500">Correct</div>
                </div>
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{replayResult.accuracyStats.incorrect}</div>
                  <div className="text-xs text-gray-500">Incorrect</div>
                </div>
                <div className="border border-gray-200 bg-white rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${replayResult.accuracyStats.accuracyPct >= 70 ? 'text-green-600' : replayResult.accuracyStats.accuracyPct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {replayResult.accuracyStats.accuracyPct}%
                  </div>
                  <div className="text-xs text-gray-500">Accuracy</div>
                </div>
              </div>
            </div>

            {/* Results Table */}
            {replayResult.results && replayResult.results.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700">
                  Evaluation Results ({replayResult.results.length})
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 text-gray-500">Week</th>
                        <th className="text-left py-2 px-3 text-gray-500">Belief</th>
                        <th className="text-center py-2 px-3 text-gray-500">Outcome</th>
                        <th className="text-center py-2 px-3 text-gray-500">Score</th>
                        <th className="text-center py-2 px-3 text-gray-500">Data W</th>
                        <th className="text-center py-2 px-3 text-gray-500">Data W+1</th>
                      </tr>
                    </thead>
                    <tbody>
                      {replayResult.results.slice(0, 26).map((r, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 px-3 font-mono">{r.calendarWeekKey.split('_to_')[0]}</td>
                          <td className="py-2 px-3">{r.beliefPrimary?.replace(/_/g, ' ') || '—'}</td>
                          <td className="py-2 px-3 text-center">
                            {r.outcome ? (
                              <span className={`px-2 py-0.5 rounded text-xs ${outcomeColors[r.outcome] || outcomeColors.uncertain}`}>
                                {r.outcome}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="py-2 px-3 text-center">{r.score?.toFixed(2) || '—'}</td>
                          <td className="py-2 px-3 text-center">
                            {r.hasWeekWData ? (
                              <span className="text-green-600">✓</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {r.hasWeekW1Data ? (
                              <span className="text-green-600">✓</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {!replayResult && !error && rollups?.totalEvaluations === 0 && (
          <div className="text-sm text-gray-600">
            <p>No belief evaluations found. Run a replay to evaluate historical beliefs against actuals.</p>
            <ul className="mt-2 text-xs text-gray-500 list-disc pl-4 space-y-1">
              <li>Fetches beliefs from historical brain_runs</li>
              <li>Uses weekly_actuals_retail/dtc for W and W+1 metrics</li>
              <li>Stores results in belief_performance table</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

